import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, BookOpen, MapPin, Save, X, Edit, Eye, Clock } from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { getBatches } from '../../firebase/collections';
import { tuitionTeacherService } from '../../firebase/tuition';
import { saveMasterTimetable, getMasterTimetable } from '../../firebase/collections';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

// Default days and periods
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEFAULT_PERIODS = [
  'Period 1 (9:00-9:45)',
  'Period 2 (9:45-10:30)',
  'Period 3 (10:30-11:15)',
  'Period 4 (11:15-12:00)',
  'Break (12:00-12:30)',
  'Period 5 (12:30-1:15)',
  'Period 6 (1:15-2:00)',
  'Period 7 (2:00-2:45)',
  'Period 8 (2:45-3:30)'
];

const MasterTimetableManagement = ({ onClose, coachingId: propCoachingId }) => {
  console.log('MasterTimetableManagement rendered with coachingId:', propCoachingId);

  const { userProfile } = useRealtime();
  const [timetable, setTimetable] = useState([]);
  const [batches, setBatches] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);
  const [selectedCell, setSelectedCell] = useState(null); // {batchId, period}
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignData, setAssignData] = useState({ subject: '', teacherId: '', room: '' });
  const [newSubjectInput, setNewSubjectInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [periods, setPeriods] = useState(DEFAULT_PERIODS);
  const [subjects, setSubjects] = useState([]);

  const isSuperAdmin = userProfile?.role === 'admin' || userProfile?.role === 'superadmin' || userProfile?.role === 'founder';
  const coachingId = propCoachingId !== undefined ? propCoachingId : (isSuperAdmin ? null : (userProfile?.coachingId || null));

  useEffect(() => {
    loadData();
  }, []);

  const normalizeSubjectsFromTeachers = (teacherList) => {
    const allSubjects = teacherList.flatMap((t) => t.subjects || []);
    return [...new Set(allSubjects)].sort();
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const batchesResult = await getBatches(coachingId);
      if (batchesResult.success) {
        setBatches(batchesResult.data || []);
      }

      const teachersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'teacher'),
        ...(coachingId ? [where('coachingId', '==', coachingId)] : [])
      );
      const teachersSnapshot = await getDocs(teachersQuery);
      const teachersList = [];
      teachersSnapshot.forEach((doc) => {
        teachersList.push({ id: doc.id, ...doc.data() });
      });
      const approvedTeachers = teachersList.filter((t) => t.status === 'approved' || !t.status);
      setTeachers(approvedTeachers);

      const timetableResult = await getMasterTimetable(coachingId);
      if (timetableResult.success) {
        const resultData = timetableResult.data || {};
        const loadedSlots = resultData.slots || [];
        const loadedPeriods = (resultData.periods && resultData.periods.length > 0) ? resultData.periods : DEFAULT_PERIODS;
        const loadedSubjects = (resultData.subjects && resultData.subjects.length > 0)
          ? resultData.subjects
          : normalizeSubjectsFromTeachers(approvedTeachers);

        setTimetable(loadedSlots);
        setPeriods(loadedPeriods);
        setSubjects(loadedSubjects);
      } else {
        setSubjects(normalizeSubjectsFromTeachers(approvedTeachers));
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const saveCurrentTimetable = async (newTimetable, newPeriods = periods, newSubjects = subjects) => {
    setSaving(true);
    try {
      const saveResult = await saveMasterTimetable(newTimetable, coachingId, {
        periods: newPeriods,
        subjects: newSubjects
      });
      if (!saveResult.success) {
        toast.error('Failed to save timetable');
        return { success: false };
      }
      setTimetable(newTimetable);
      setPeriods(newPeriods);
      setSubjects(newSubjects);
      toast.success('Master timetable saved');
      return { success: true };
    } catch (error) {
      toast.error('Failed to save timetable');
      return { success: false };
    } finally {
      setSaving(false);
    }
  };

  const getSlotForCell = (batchId, period) => {
    return timetable.find((slot) => slot.day === selectedDay && slot.period === period && slot.classId === batchId);
  };

  const handleCellClick = (batchId, period) => {
    if (period.toLowerCase().includes('break')) return;

    const existingSlot = getSlotForCell(batchId, period);
    setSelectedCell({ batchId, period });
    setAssignData({
      subject: existingSlot?.subject || '',
      teacherId: existingSlot?.teacherId || '',
      room: existingSlot?.room || ''
    });
    setShowAssignModal(true);
  };

  const getSubjectsForClass = () => {
    if (subjects && subjects.length > 0) {
      return subjects;
    }
    return normalizeSubjectsFromTeachers(teachers);
  };

  useEffect(() => {
    if ((!subjects || subjects.length === 0) && teachers && teachers.length > 0) {
      setSubjects(normalizeSubjectsFromTeachers(teachers));
    }
  }, [teachers, subjects]);

  const handleAssign = async () => {
    if (!assignData.subject || !assignData.teacherId) {
      toast.error('Subject and Teacher are required');
      return;
    }

    const teacherConflict = timetable.find((slot) =>
      slot.day === selectedDay &&
      slot.period === selectedCell.period &&
      slot.teacherId === assignData.teacherId &&
      slot.classId !== selectedCell.batchId
    );

    if (teacherConflict) {
      toast.error('Teacher is already assigned to another class at this time');
      return;
    }

    const updatedTimetable = [
      ...timetable.filter((slot) =>
        !(slot.day === selectedDay && slot.period === selectedCell.period && slot.classId === selectedCell.batchId)
      ),
      {
        day: selectedDay,
        period: selectedCell.period,
        classId: selectedCell.batchId,
        subject: assignData.subject,
        teacherId: assignData.teacherId,
        room: assignData.room
      }
    ];

    const saveResult = await saveCurrentTimetable(updatedTimetable, periods, subjects);
    if (!saveResult.success) {
      return;
    }

    setShowAssignModal(false);
    toast.success('Assignment saved');
  };

  const handleRemove = async () => {
    const updatedTimetable = timetable.filter((slot) =>
      !(slot.day === selectedDay && slot.period === selectedCell.period && slot.classId === selectedCell.batchId)
    );

    await saveCurrentTimetable(updatedTimetable, periods, subjects);

    setShowAssignModal(false);
    toast.success('Assignment removed');
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find((t) => t.id === teacherId);
    return teacher ? teacher.name : 'Unknown';
  };

  const getClassName = (classId) => {
    const batch = batches.find((b) => b.id === classId);
    return batch ? batch.name : 'Unknown';
  };

  const handlePeriodLabelChange = (index, value) => {
    const newPeriods = [...periods];
    newPeriods[index] = value;
    setPeriods(newPeriods);
  };

  const handleAddPeriod = () => {
    setPeriods([...periods, `Period ${periods.length + 1} (start-end)`]);
  };

  const handleRemovePeriod = (index) => {
    const newPeriods = periods.filter((_, i) => i !== index);
    setPeriods(newPeriods);
  };

  const handleAddSubject = async () => {
    const trimmed = newSubjectInput.trim();
    if (!trimmed) {
      toast.error('Please enter a valid subject name');
      return;
    }
    if (subjects.includes(trimmed)) {
      toast.error('Subject already exists');
      return;
    }
    const updated = [...subjects, trimmed].sort();
    setSubjects(updated);
    setNewSubjectInput('');

    // Persist immediately so new subject is available for everyone
    const result = await saveCurrentTimetable(timetable, periods, updated);
    if (!result.success) {
      toast.error('Could not save new subject in realtime');
    }
  };

  const handleRemoveSubject = async (subjectToRemove) => {
    const updated = subjects.filter((subj) => subj !== subjectToRemove);
    setSubjects(updated);

    const result = await saveCurrentTimetable(timetable, periods, updated);
    if (!result.success) {
      toast.error('Could not remove subject in realtime');
    }
  };

  const handleSavePeriodSubjectConfig = async () => {
    await saveCurrentTimetable(timetable, periods, subjects);
    toast.success('Periods and subjects configuration saved');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-lg max-w-7xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Master Timetable Management</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Period Timings</h3>
              <div className="space-y-2">
                {periods.map((period, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      value={period}
                      onChange={(e) => handlePeriodLabelChange(idx, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      onClick={() => handleRemovePeriod(idx)}
                      className="px-3 py-2 border rounded-lg text-sm text-red-600 dark:text-red-300 border-red-200 dark:border-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleAddPeriod}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Period
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Subjects (configurable)</h3>
              <div className="flex gap-2 mb-3">
                <input
                  value={newSubjectInput}
                  onChange={(e) => setNewSubjectInput(e.target.value)}
                  placeholder="Add new subject"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                />
                <button
                  onClick={handleAddSubject}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Add
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {getSubjectsForClass().length === 0 && <p className="text-gray-500">No subjects available.</p>}
                {getSubjectsForClass().map((subj) => (
                  <div key={subj} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-md px-3 py-2 mb-2">
                    <span className="text-sm text-gray-800 dark:text-gray-100">{subj}</span>
                    <button
                      onClick={() => handleRemoveSubject(subj)}
                      className="text-red-600 dark:text-red-300 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={handleSavePeriodSubjectConfig}
            className="px-4 py-2 mb-6 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Periods & Subjects'}
          </button>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select Day</label>
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              {DAYS.map((day) => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300 dark:border-gray-600">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700">
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Class</th>
                  {periods.map((period) => (
                    <th key={period} className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center min-w-[150px]">
                      {period}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <tr key={batch.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 font-medium">{batch.name}</td>
                    {periods.map((period) => {
                      const slot = getSlotForCell(batch.id, period);
                      const isBreak = period.toLowerCase().includes('break');
                      return (
                        <td
                          key={`${batch.id}-${period}`}
                          className={`border border-gray-300 dark:border-gray-600 px-2 py-2 min-h-[80px] ${
                            isBreak
                              ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                              : 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20'
                          }`}
                          onClick={() => !isBreak && handleCellClick(batch.id, period)}
                        >
                          {slot ? (
                            <div className="text-sm">
                              <div className="font-medium">{slot.subject}</div>
                              <div className="text-gray-600 dark:text-gray-400">{getTeacherName(slot.teacherId)}</div>
                              {slot.room && <div className="text-xs text-gray-500 dark:text-gray-500">Room: {slot.room}</div>}
                            </div>
                          ) : isBreak ? (
                            <div className="text-gray-500 text-sm text-center">Break</div>
                          ) : (
                            <div className="text-gray-400 text-sm">Click to assign</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Teacher Timetable View ({selectedDay})</h3>
              <div className="space-y-2">
                {teachers.map((teacher) => {
                  const teacherSlots = timetable
                    .filter((slot) => slot.teacherId === teacher.id && slot.day === selectedDay)
                    .sort((a, b) => periods.indexOf(a.period) - periods.indexOf(b.period));
                  if (teacherSlots.length === 0) return null;
                  return (
                    <div key={teacher.id} className="border rounded-lg p-4">
                      <h4 className="font-medium">{teacher.name}</h4>
                      <div className="mt-2 space-y-1">
                        {teacherSlots.map((slot, idx) => (
                          <div key={idx} className="text-sm text-gray-600 dark:text-gray-400">
                            {slot.period}: {slot.subject} ({getClassName(slot.classId)}){slot.room ? ` - Room ${slot.room}` : ''}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Class Timetable Summary ({selectedDay})</h3>
              <div className="space-y-2">
                {batches.map((batch) => {
                  const batchSlots = timetable
                    .filter((slot) => slot.classId === batch.id && slot.day === selectedDay)
                    .sort((a, b) => periods.indexOf(a.period) - periods.indexOf(b.period));
                  if (batchSlots.length === 0) return null;
                  return (
                    <div key={batch.id} className="border rounded-lg p-4">
                      <h4 className="font-medium">{batch.name}</h4>
                      <div className="mt-2 space-y-1">
                        {batchSlots.map((slot, idx) => (
                          <div key={idx} className="text-sm text-gray-600 dark:text-gray-400">
                            {slot.period}: {slot.subject} - {getTeacherName(slot.teacherId)}{slot.room ? ` (Room ${slot.room})` : ''}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {showAssignModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-60">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md"
              >
                <h3 className="text-lg font-semibold mb-4">
                  Assign to {getClassName(selectedCell?.batchId)} - {selectedCell?.period} ({selectedDay})
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Subject</label>
                    <select
                      value={assignData.subject}
                      onChange={(e) => setAssignData({ ...assignData, subject: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select subject...</option>
                      {getSubjectsForClass().map((subject) => (
                        <option key={subject} value={subject}>{subject}</option>
                      ))}
                    </select>
                    <div className="mt-2 flex gap-2">
                      <input
                        value={newSubjectInput}
                        onChange={(e) => setNewSubjectInput(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                        placeholder="Add subject to bank"
                      />
                      <button
                        onClick={handleAddSubject}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Plus
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Teacher</label>
                    <select
                      value={assignData.teacherId}
                      onChange={(e) => setAssignData({ ...assignData, teacherId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select teacher...</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Room (Optional)</label>
                    <input
                      type="text"
                      value={assignData.room}
                      onChange={(e) => setAssignData({ ...assignData, room: e.target.value })}
                      placeholder="e.g. Room 101"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    onClick={() => setShowAssignModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  {getSlotForCell(selectedCell.day, selectedCell.period) && (
                    <button onClick={handleRemove} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                      Remove
                    </button>
                  )}
                  <button onClick={handleAssign} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Assign
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default MasterTimetableManagement;
